import { BBox } from '@turf/helpers';
import i18next from 'i18next';

import { GeoFilter, GeoFilterType } from '../../contexts/FilterContext';
import { GeoField, PeopleSnaphostGeoResult, PersonFilterInput } from '../../types/api.graphql';
import { hashCode } from '../helpers';
import { DrawProps, GeoFilterHelper, MarkerProps } from './GeoFilterHelper';

export interface GeoFilterListData {
  field: GeoField;
  values: string[];
}

type ZipCodeCount = { zip: string; count: number };

const ZIP4_DASHED_REGEX = /^\d{5}-\d{4}$/;
const ZIP4_RAW9_REGEX = /^\d{9}$/;

const getZip4BaseZip = (value: string) => {
  const trimmed = value.trim();
  if (ZIP4_DASHED_REGEX.test(trimmed) || ZIP4_RAW9_REGEX.test(trimmed)) return trimmed.slice(0, 5);
  return null;
};

const isString = (value: string | null): value is string => Boolean(value);

export default class ListGeoFilterHelper extends GeoFilterHelper<GeoFilterListData> {
  generateId(): number {
    const valuesStr = this.geoFilter.data.values?.slice(0, 10).join(',') || '';
    const valuesLength = this.geoFilter.data.values?.length || 0;
    return hashCode(
      `${this.geoFilter.data.field},${valuesStr},${valuesLength},${(this.geoFilter.isParent || false).toString()},${(
        this.geoFilter.negate || false
      ).toString()}`,
    );
  }

  isEqual(geoFilter: GeoFilter): boolean {
    if (!super.isEqual(geoFilter)) return false;

    if (geoFilter.data.field !== this.geoFilter.data.field) return false;
    if (geoFilter.negate !== this.geoFilter.negate) return false;
    if (geoFilter.data.values?.length !== this.geoFilter.data.values?.length) return false;
    for (let i = 0; i < (geoFilter.data.values?.length || 0); i++) {
      if (geoFilter.data.values[i] !== this.geoFilter.data.values[i]) return false;
    }
    return true;
  }

  toGraphQLGeoFilter(skipExcludedZips?: boolean): PersonFilterInput {
    return {
      geoFilter: {
        operator: {
          in: {
            field: this.geoFilter.data.field,
            values: this.geoFilter.data.values,
          },
        },
        negate: this.geoFilter.negate || false,
        ...(!skipExcludedZips && { excludedZipCodes: this.geoFilter.excludedZipCodes }),
      },
    };
  }

  generateIdForGeoFilterGraphQl(): number {
    const valuesStr = this.geoFilterGraphQL.operator.in?.values?.slice(0, 10).join(',') || '';
    const valuesLength = this.geoFilterGraphQL.operator.in?.values?.length || 0;
    return hashCode(`${this.geoFilterGraphQL.operator.in?.field},${valuesStr},${valuesLength}`);
  }

  fromGraphQLGeoFilter(): GeoFilter<GeoFilterListData> {
    const data: GeoFilterListData = {
      field: this.geoFilterGraphQL.operator.in?.field,
      values: this.geoFilterGraphQL.operator.in?.values || [],
    };
    return {
      id: this.generateIdForGeoFilterGraphQl(),
      type: GeoFilterType.List,
      data,
      negate: this.geoFilterGraphQL.negate || false,
      excludedZipCodes: this.geoFilterGraphQL.excludedZipCodes,
    };
  }

  getInfoText(): string {
    const fieldLabel =
      this.geoFilter.data.field === GeoField.Dma
        ? i18next.t('geoFilters.listFieldDma')
        : i18next.t('geoFilters.listFieldZip');
    const condition = this.geoFilter.negate
      ? i18next.t('geoFilters.listConditionNotIn')
      : i18next.t('geoFilters.listConditionIn');
    return `${fieldLabel} ${condition} (${this.geoFilter.data.values?.length || 0} values)`;
  }

  private zipCodesFromGeoResult = (geoResult: PeopleSnaphostGeoResult, countByPostalCode: object): ZipCodeCount[] => {
    const countsByPostalCode = countByPostalCode as Record<string, number>;
    const zipCodes = geoResult.postalCodes.filter(isString).map((postalCode) => ({
      zip: postalCode,
      count: countsByPostalCode[postalCode] || 0,
    }));

    if (this.geoFilter.data.field !== GeoField.Zip) return zipCodes;

    const values = this.geoFilter.data.values || [];
    const wholeZipSet = new Set(values.filter((value) => !getZip4BaseZip(value)).map((value) => value.trim()));
    const zip4BaseZips = [
      ...new Set(
        values
          .map(getZip4BaseZip)
          .filter(isString)
          .filter((zip) => !wholeZipSet.has(zip)),
      ),
    ];

    if (zip4BaseZips.length !== 1) return zipCodes;

    const wholeZipCount = [...wholeZipSet].reduce((sum, zip) => sum + (countsByPostalCode[zip] || 0), 0);
    const zip4Count = Math.max(geoResult.count - wholeZipCount, 0);
    return zipCodes.map((zipCode) => (zipCode.zip === zip4BaseZips[0] ? { ...zipCode, count: zip4Count } : zipCode));
  };

  geoFilterFromGeoResult = (geoResult: PeopleSnaphostGeoResult, countByPostalCode: object): GeoFilter => ({
    ...this.geoFilter,
    id: this.geoFilter.id || this.generateId(),
    zipCodes: this.zipCodesFromGeoResult(geoResult, countByPostalCode),
    count: geoResult.count,
  });

  getCenter() {
    return undefined;
  }

  getBoundingBox(): BBox {
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  drawLayer(props: DrawProps) {
    return {};
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  drawMarker(props: MarkerProps) {
    return undefined;
  }
}
